/**
 * pieceThemes.js — Duelo chess piece theming.
 *
 * react-chessboard accepts a `customPieces` prop: an object where each key
 * is a piece code (wP, wN, wB, wR, wQ, wK, bP, bN, bB, bR, bQ, bK) and
 * each value is a render function: ({ squareWidth }) => React element.
 *
 * USAGE:
 *   import { getPieceTheme, PIECE_THEME_NAMES } from '@/themes/pieceThemes'
 *
 *   // null = use react-chessboard default pieces
 *   const customPieces = getPieceTheme('default')
 *
 * To add a custom piece set, add a key to PIECE_THEMES below and return
 * an <img> or <svg> element sized to squareWidth × squareWidth.
 *
 * Example (loading PNG sprites from /public/pieces/custom/):
 *
 *   custom: buildImageTheme('/pieces/custom', '.png'),
 */

const PIECE_CODES = ['wP','wN','wB','wR','wQ','wK','bP','bN','bB','bR','bQ','bK']

/**
 * Build a customPieces map that loads images from a base path.
 * filenames are expected to be e.g. wP.svg, bK.svg …
 */
function buildImageTheme(basePath, ext = '.svg') {
  return Object.fromEntries(
    PIECE_CODES.map((code) => [
      code,
      ({ squareWidth }) => (
        <img
          src={`${basePath}/${code}${ext}`}
          alt={code}
          width={squareWidth}
          height={squareWidth}
          draggable={false}
          style={{ userSelect: 'none' }}
        />
      ),
    ])
  )
}

/**
 * Registered piece themes.
 * 'default' → null (react-chessboard built-in pieces)
 * Add more entries as your piece assets become available.
 */
const PIECE_THEMES = {
  /** react-chessboard built-in pieces */
  default: null,

  // Example custom SVG set (uncomment & add assets to /public/pieces/duelo/):
  // duelo: buildImageTheme('/pieces/duelo', '.svg'),
}

export const PIECE_THEME_NAMES = Object.keys(PIECE_THEMES)

/**
 * Returns the customPieces prop value for react-chessboard,
 * or undefined if the theme uses built-in pieces.
 */
export function getPieceTheme(themeName = 'default') {
  return PIECE_THEMES[themeName] ?? undefined
}

export { buildImageTheme }
